import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import uuidv4 from 'uuid/v4';
import { StoriesAddPlus } from '../../assets/images';
import { blockOfflineMsgAction, convertVideoFile, getMessageType } from '../../Helpers/Utility';
import SDK from '../SDK';
import Modal from '../WebChat/Common/Modal';
import MediaPreview from '../WebChat/MediaPreview';
import FileUpload from './FileUpload';
import MyStories from './MyStories';
import './Stories.scss';
import StoriesHeader from './StoriesHeader';
import StoriesList from './StoriesList';
import StoryMediaPreview from './StoryPreview/StoryMediaPreview';

const StoriesSection = () => {
    const fileInputRef = useRef(null);

    const myStories = useSelector(state => state.stories.myStories);

    const [selectedFiles, setSelectedFiles] = useState({});
    const [previewOpen, setPreviewOpen] = useState(false);
    const [isShowStoryPreviewMedia, setIsShowStoryPreviewMedia] = useState(false);
    const [selectedStoryPreviewData, setSelectedStoryPreviewData] = useState(null);
    const [isMyStory, setIsMyStory] = useState(false);

    const hasStories = myStories?.length > 0;

    const openGallery = () => {
        if (blockOfflineMsgAction()) return;
        fileInputRef.current?.click();
    };

    const handleStoryPreviewClose = () => {
        setIsShowStoryPreviewMedia(false);
        setIsMyStory(false);
    };

    const handleShowStoryPreview = data => {
        setSelectedStoryPreviewData(data);
        setIsShowStoryPreviewMedia(true);
    };

    const handleFileSelect = async (e, mediaType) => {
        const selectedFilesList = [...e.target.files];

        if (!selectedFilesList.length) return;

        const files = await Promise.all(
            selectedFilesList.map(async file => {
                const shouldConvert = file.type.startsWith('video/') && file.type !== 'video/mp4';

                return shouldConvert ? await convertVideoFile(file) : file;
            }),
        );

        setSelectedFiles({
            filesId: uuidv4(),
            files: files.filter(Boolean),
            mediaType,
        });

        setPreviewOpen(true);
    };

    const resetInputValue = e => {
        e.target.value = '';
    };

    const closePreview = () => {
        setPreviewOpen(false);
        setSelectedFiles({});
    };

    const sendMediaMessage = async (files, messageType) => {
        if (messageType === 'media') {
            // For Local Render Process - Needs to be Handled with "Asynchronous"
            for (const file of files) {
                const storyId = uuidv4();
                const { caption = '' } = file;

                const regex = /<span\s[^>]*>@([^<]+)<\/span>/g;
                let captionText = caption.replace(regex, '@[?]');
                file.caption = captionText;

                const msgType = getMessageType(file.type, file);

                let fileOptions = {
                    file,
                    storyId,
                    caption,
                };

                await SDK.sendStoryPostMessage({
                    messageType: msgType,
                    fileMessageParams: fileOptions,
                });
            }
        }
        closePreview();
    };

    return (
        <>
            <div className="stories-section">
                <StoriesHeader />

                <ul className="stories-list-ul" id="scrollableUl-stories">
                    <MyStories
                        openGallery={openGallery}
                        handleShowStoryPreview={data => {
                            handleShowStoryPreview(data);
                            setIsMyStory(true);
                        }}
                    />

                    <StoriesList
                        handleShowStoryPreview={data => {
                            handleShowStoryPreview(data);
                            setIsMyStory(false);
                        }}
                    />
                </ul>

                {hasStories && (
                    <div className="add-stories-plus-container" onClick={openGallery}>
                        <button className="add-stories-plus-btn">
                            <i>
                                <StoriesAddPlus />
                            </i>
                        </button>
                    </div>
                )}

                <FileUpload
                    fileInputRef={fileInputRef}
                    resetInputValue={resetInputValue}
                    handleFileSelect={handleFileSelect}
                />
            </div>

            {previewOpen && (
                <Modal containerId="container">
                    <MediaPreview
                        type="story"
                        selectedFiles={selectedFiles}
                        onClickSend={e => sendMediaMessage(e, 'media')}
                        onClosePreview={closePreview}
                    />
                </Modal>
            )}
            {isShowStoryPreviewMedia && (
                <Modal containerId="container">
                    <StoryMediaPreview
                        selectedMessageData={selectedStoryPreviewData}
                        handleMediaClose={handleStoryPreviewClose}
                        isMyStory={isMyStory}
                    />
                </Modal>
            )}
        </>
    );
};

export default StoriesSection;
